import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { Button } from "./ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"
import { Checkbox } from "./ui/checkbox"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Separator } from "./ui/separator"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Alert, AlertDescription } from "./ui/alert"
import {
  Leaf,
  Mail,
  Lock,
  User,
  MapPin,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowLeft,
} from "lucide-react"
import { cn } from "../lib/utils"
import { useAuth } from "../contexts/AuthContext"
import LanguageSwitcher from "./LanguageSwitcher"

const lithuanianCities = [
  "Vilnius",
  "Kaunas",
  "Klaipėda",
  "Šiauliai",
  "Panevėžys",
  "Alytus",
  "Marijampolė",
  "Mažeikiai",
  "Jonava",
  "Utena",
  "Kėdainiai",
  "Telšiai",
  "Visaginas",
  "Tauragė",
  "Ukmergė",
  "Plungė",
  "Kretinga",
  "Šilutė",
  "Radviliškis",
  "Palanga",
]

interface FormData {
  fullName: string
  email: string
  password: string
  confirmPassword: string
  city: string
  acceptTerms: boolean
}

interface FormErrors {
  fullName?: string
  email?: string
  password?: string
  confirmPassword?: string
  acceptTerms?: string
  general?: string
}

export default function Register() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { register } = useAuth()

  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    city: "",
    acceptTerms: false,
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

  const validateField = (name: string, value: string | boolean, currentPassword?: string) => {
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors }

      switch (name) {
        case "fullName":
          if (!value || (value as string).trim().length < 2) {
            newErrors.fullName = t("errors.fullNameTooShort")
          } else if (!(value as string).includes(" ")) {
            newErrors.fullName = t("errors.fullNameRequired")
          } else {
            delete newErrors.fullName
          }
          break

        case "email":
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          if (!value) {
            newErrors.email = t("errors.emailRequired")
          } else if (!emailRegex.test(value as string)) {
            newErrors.email = t("errors.emailInvalid")
          } else {
            delete newErrors.email
          }
          break

        case "password":
          if (!value) {
            newErrors.password = t("errors.passwordRequired")
          } else if ((value as string).length < 8) {
            newErrors.password = t("errors.passwordTooShort")
          } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value as string)) {
            newErrors.password = t("errors.passwordComplexity")
          } else {
            delete newErrors.password
          }
          break

        case "confirmPassword":
          if (!value) {
            newErrors.confirmPassword = t("errors.confirmPasswordRequired")
          } else if (value !== (currentPassword || formData.password)) {
            newErrors.confirmPassword = t("errors.passwordsDoNotMatch")
          } else {
            delete newErrors.confirmPassword
          }
          break

        case "acceptTerms":
          if (!value) {
            newErrors.acceptTerms = t("errors.termsRequired")
          } else {
            delete newErrors.acceptTerms
          }
          break
      }

      return newErrors
    })
  }

  const handleInputChange = (name: string, value: string | boolean) => {
    setFormData((prev) => {
      const newFormData = { ...prev, [name]: value }
      
      // Run validation immediately for all fields
      validateField(name, value)
      
      // Special case: if password changes, also validate confirm password with new password
      if (name === "password") {
        validateField("confirmPassword", newFormData.confirmPassword, value as string)
      }
      
      return newFormData
    })
    
    // Mark field as touched if not already
    if (!touchedFields.has(name)) {
      setTouchedFields((prev) => new Set(prev).add(name))
    }

    // Clear general error when user starts typing
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }))
    }
  }

  const handleBlur = (name: string) => {
    // Just mark field as touched - validation already runs on input change
    setTouchedFields((prev) => new Set(prev).add(name))
  }

  const getFieldStatus = (fieldName: string) => {
    if (!touchedFields.has(fieldName)) return "default"
    if (errors[fieldName as keyof FormErrors]) return "error"
    
    // Show success for valid fields that have content
    const value = formData[fieldName as keyof FormData]
    if (typeof value === "string" && value.trim() !== "") {
      return "success"
    }
    if (typeof value === "boolean" && value === true) {
      return "success"
    }
    
    return "default"
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Mark all fields as touched
    setTouchedFields((prev) => {
      const newTouched = new Set(prev)
      Object.keys(formData).forEach((key) => newTouched.add(key))
      return newTouched
    })

    // Validate all fields
    Object.keys(formData).forEach((key) => {
      validateField(key, formData[key as keyof FormData])
    })

    // Check for errors
    if (Object.keys(errors).length > 0) {
      return
    }

    setIsLoading(true)

    try {
      const result = await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.fullName,
        preferred_language: t('common.language', { returnObjects: false }) || 'lt',
        location: formData.city ? { city: formData.city } : undefined
      });

      if (result.success) {
        setIsSuccess(true);
      } else {
        // Handle registration error
        console.error("Registration failed:", result.message);
        
        // Handle different error types
        if (result.message?.includes('already exists') || result.message?.includes('email')) {
          setErrors({ email: t('errors.emailAlreadyExists') });
        } else if (result.message?.includes('Validation failed') || result.message?.includes('invalid')) {
          setErrors({ general: t('errors.invalidData') });
        } else {
          setErrors({ general: result.message || t('errors.registrationFailed') });
        }
      }
    } catch (error: any) {
      console.error("Registration failed:", error);
      
      // Handle different error types
      if (error.response?.status === 400) {
        if (error.response?.data?.message?.includes('already exists')) {
          setErrors({ email: t('errors.emailAlreadyExists') });
        } else {
          setErrors({ general: t('errors.invalidData') });
        }
      } else {
        setErrors({ general: t('errors.unexpectedError') });
      }
    } finally {
      setIsLoading(false);
    }
  }

  const handleSocialLogin = (provider: string) => {
    console.log(`Logging in with ${provider}`)
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleSignIn = () => {
    navigate("/login")
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 flex items-center justify-center p-4 relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=60 height=60 viewBox=0 0 60 60 xmlns=http://www.w3.org/2000/svg%3E%3Cg fill=none fillRule=evenodd%3E%3Cg fill=%2310b981 fillOpacity=0.05%3E%3Cpath d=M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm0 0c0 11.046 8.954 20 20 20s20-8.954 20-20-8.954-20-20-20-20 8.954-20 20z/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] bg-repeat"></div>

        {/* Language Switcher */}
        <LanguageSwitcher />

        <Card className="w-full max-w-md border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t("registration.successTitle")}</h2>
            <p className="text-gray-600 mb-6">
              {t("registration.successMessage")}
            </p>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleSignIn}>
              {t("registration.signInToAccount")}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 flex items-center justify-center p-4 relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=60 height=60 viewBox=0 0 60 60 xmlns=http://www.w3.org/2000/svg%3E%3Cg fill=none fillRule=evenodd%3E%3Cg fill=%2310b981 fillOpacity=0.05%3E%3Cpath d=M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm0 0c0 11.046 8.954 20 20 20s20-8.954 20-20-8.954-20-20-20-20 8.954-20 20z/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] bg-repeat"></div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-20 w-32 h-32 bg-emerald-200/20 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-40 h-40 bg-green-200/20 rounded-full blur-xl animate-pulse delay-1000"></div>
      <div className="absolute top-1/2 left-10 w-24 h-24 bg-teal-200/20 rounded-full blur-xl animate-pulse delay-500"></div>

      {/* Language Switcher */}
      <LanguageSwitcher />

      <div className="relative w-full max-w-md">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-4 text-emerald-700 hover:bg-emerald-50"
          onClick={handleBack}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("registration.back")}
        </Button>

        <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center pb-6">
            {/* Logo */}
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-lg">
                <Leaf className="h-6 w-6" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-emerald-800">{t("branding.appName")}</span>
                <span className="text-xs text-emerald-600">{t("registration.smartGardenManagement")}</span>
              </div>
            </div>

            <CardTitle className="text-2xl font-bold text-gray-900">{t("registration.title")}</CardTitle>
            <CardDescription className="text-gray-600">{t("registration.subtitle")}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* General Error Alert */}
            {errors.general && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">{errors.general}</AlertDescription>
              </Alert>
            )}

            {/* Social Login */}
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full border-gray-200 hover:bg-gray-50 bg-transparent"
                onClick={() => handleSocialLogin("Google")}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {t("registration.continueWithGoogle")}
              </Button>

              <Button
                variant="outline"
                className="w-full border-gray-200 hover:bg-gray-50 bg-transparent"
                onClick={() => handleSocialLogin("Facebook")}
              >
                <svg className="w-5 h-5 mr-2" fill="#1877F2" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                {t("registration.continueWithFacebook")}
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">{t("registration.or")}</span>
              </div>
            </div>

            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-sm font-medium text-gray-700">
                  {t("registration.fullNameLabel")}
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="fullName"
                    type="text"
                    placeholder={t("registration.fullNamePlaceholder")}
                    value={formData.fullName}
                    onChange={(e) => handleInputChange("fullName", e.target.value)}
                    onBlur={() => handleBlur("fullName")}
                    className={cn(
                      "pl-10 pr-10",
                      getFieldStatus("fullName") === "error" && "border-red-500 focus:border-red-500",
                      getFieldStatus("fullName") === "success" && "border-emerald-500 focus:border-emerald-500",
                    )}
                  />
                  {getFieldStatus("fullName") === "success" && (
                    <CheckCircle2 className="absolute right-3 top-3 h-4 w-4 text-emerald-500" />
                  )}
                  {getFieldStatus("fullName") === "error" && (
                    <AlertCircle className="absolute right-3 top-3 h-4 w-4 text-red-500" />
                  )}
                </div>
                {errors.fullName && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.fullName}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  {t("registration.emailLabel")}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t("registration.emailPlaceholder")}
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onBlur={() => handleBlur("email")}
                    className={cn(
                      "pl-10 pr-10",
                      getFieldStatus("email") === "error" && "border-red-500 focus:border-red-500",
                      getFieldStatus("email") === "success" && "border-emerald-500 focus:border-emerald-500",
                    )}
                  />
                  {getFieldStatus("email") === "success" && (
                    <CheckCircle2 className="absolute right-3 top-3 h-4 w-4 text-emerald-500" />
                  )}
                  {getFieldStatus("email") === "error" && (
                    <AlertCircle className="absolute right-3 top-3 h-4 w-4 text-red-500" />
                  )}
                </div>
                {errors.email && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  {t("registration.passwordLabel")}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("registration.passwordPlaceholder")}
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    onBlur={() => handleBlur("password")}
                    className={cn(
                      "pl-10 pr-10",
                      getFieldStatus("password") === "error" && "border-red-500 focus:border-red-500",
                      getFieldStatus("password") === "success" && "border-emerald-500 focus:border-emerald-500",
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
                {errors.password && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  {t("registration.confirmPasswordLabel")}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t("registration.passwordPlaceholder")}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                    onBlur={() => handleBlur("confirmPassword")}
                    className={cn(
                      "pl-10 pr-10",
                      getFieldStatus("confirmPassword") === "error" && "border-red-500 focus:border-red-500",
                      getFieldStatus("confirmPassword") === "success" && "border-emerald-500 focus:border-emerald-500",
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label htmlFor="city" className="text-sm font-medium text-gray-700">
                  {t("registration.cityLabel")} <span className="text-gray-500">{t("registration.cityOptional")}</span>
                </Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                  <Select 
                    value={formData.city} 
                    onValueChange={(value) => handleInputChange("city", value)}
                  >
                    <SelectTrigger className="pl-10">
                      <SelectValue placeholder={t("registration.cityPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                    {lithuanianCities.map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Terms */}
              <div className="space-y-2">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="acceptTerms"
                    checked={formData.acceptTerms}
                    onCheckedChange={(checked: boolean) => handleInputChange("acceptTerms", checked)}
                    onBlur={() => handleBlur("acceptTerms")}
                    className={cn("mt-1", errors.acceptTerms && "border-red-500")}
                  />
                  <Label htmlFor="acceptTerms" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                    {t("registration.termsAccept")}{" "}
                    <a href="#" className="text-emerald-600 hover:text-emerald-700 underline">
                      {t("registration.termsOfUse")}
                    </a>{" "}
                    {t("registration.and")}{" "}
                    <a href="#" className="text-emerald-600 hover:text-emerald-700 underline">
                      {t("registration.privacyPolicy")}
                    </a>
                  </Label>
                </div>
                {errors.acceptTerms && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.acceptTerms}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("registration.registering")}
                  </>
                ) : (
                  t("registration.register")
                )}
              </Button>
            </form>

            {/* Login Link */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                {t("registration.alreadyHaveAccountText")}{" "}
                <button
                  type="button"
                  onClick={handleSignIn}
                  className="text-emerald-600 hover:text-emerald-700 font-medium underline"
                >
                  {t("registration.signIn")}
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
} 